import { PagoMiembroFields, pagoMiembroValido, pagoMiembroPayload } from "./PagoMiembroFields";
import React, { useEffect, useMemo, useState } from "react";
import {
	Autocomplete,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	MenuItem,
	TextField,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { AlertError } from "../../utils/componentes-utiles/AlertError";
import { getConceptosContabilidadActivos, getTiposTickets, postAsistentes } from "../../services/apis";
import { getEventType, getOpcionesEncargadosEvento, getPreciosTicketsMap } from "./eventosUtils";

const normalizarHora = (hora) => (hora ? String(hora).slice(0, 5) : "");

export const EventoSimpleEditDialog = ({ open, onClose, evento, opTrabajadores = [], onSaved }) => {
	const tipo = getEventType(evento.id_tipo_evento);
	const TipoIcon = tipo.icon;
	const requiereEncargado = Number(evento.id_tipo_evento) !== 4;
	const usaPreciosTickets = Number(evento.id_tipo_evento) === 1;
	const initialForm = useMemo(() => ({
		miembro_paga: Number(evento?.miembro_paga || 0),
		precio_miembro: evento?.precio_miembro ?? "",
		asunto: evento.asunto || "",
		descripcion: evento.descripcion || "",
		fecha: evento.fecha || "",
		hora_inicio: normalizarHora(evento.hora_inicio),
		hora_final: normalizarHora(evento.hora_final),
		invitados_penya: Number(evento.invitados_penya || 0),
		precios_tickets: getPreciosTicketsMap(evento),
		usuarios_encargados: getOpcionesEncargadosEvento(evento, opTrabajadores),
		id_apartado: evento.id_apartado || "",
	}), [evento, opTrabajadores]);

	const [form, setForm] = useState(initialForm);
	const [tiposTicketsPagan, setTiposTicketsPagan] = useState([]);
	const [conceptosContables, setConceptosContables] = useState([]);
	const [guardando, setGuardando] = useState(false);
	const [error, setError] = useState(false);
	const [mensajeError, setMensajeError] = useState("Falta omplir algun camp obligatori");

	const updateField = (field, value) => {
		setForm((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	useEffect(() => {
		if (!open) return;
		if (typeof getConceptosContabilidadActivos === "function") {
			getConceptosContabilidadActivos().then((response) => setConceptosContables(response?.data || []));
		}
	}, [open]);

	useEffect(() => {
		setForm(initialForm);
	}, [initialForm]);

	useEffect(() => {
		const cargarTiposTickets = async () => {
			if (!open || !usaPreciosTickets) {
				setTiposTicketsPagan([]);
				return;
			}

			const anyo = Number(String(form.fecha || "").slice(0, 4)) || new Date().getFullYear();
			try {
				const response = await getTiposTickets(1, anyo);
				const tiposPagan = (response?.data || []).filter((tipo) => Number(tipo.paga) === 1);
				setTiposTicketsPagan(tiposPagan);
				setForm((prev) => {
					const precios = { ...prev.precios_tickets };
					tiposPagan.forEach((tipo) => {
						const idTipo = Number(tipo.id);
						if (precios[idTipo] === undefined) {
							precios[idTipo] = Number(evento.precio_persona || 0) || "";
						}
					});

					return {
						...prev,
						precios_tickets: precios,
					};
				});
			} catch (err) {
				console.error("Error en carregar tipus de tiquets:", err);
				setTiposTicketsPagan([]);
			}
		};

		cargarTiposTickets();
	}, [open, usaPreciosTickets, form.fecha, evento.precio_persona]);

	const updatePrecioTicket = (idTipoTicket, value) => {
		setForm((prev) => ({
			...prev,
			precios_tickets: {
				...prev.precios_tickets,
				[Number(idTipoTicket)]: value,
			},
		}));
	};

	const preciosTicketsPayload = tiposTicketsPagan.map((tipo) => ({
		id_tipo_ticket: Number(tipo.id),
		precio: Number(form.precios_tickets[Number(tipo.id)] || 0),
	}));
	const tienePreciosCargados = usaPreciosTickets && tiposTicketsPagan.length > 0;

	const guardar = async () => {
		if (!pagoMiembroValido(form)) return;
		if (!form.asunto.trim() || !form.fecha || !form.hora_inicio || (requiereEncargado && form.usuarios_encargados.length === 0)) {
			setMensajeError("El nom, la data, l'hora d'inici i els encarregats són obligatoris");
			setError(true);
			return;
		}

		setGuardando(true);
		try {
			const response = await postAsistentes({
				id_evento: evento.id,
				asistentes: evento.asistentes || [],
				evento: {
					...pagoMiembroPayload(form),
					asunto: form.asunto.trim(),
					descripcion: form.descripcion.trim(),
					fecha: form.fecha,
					hora_inicio: form.hora_inicio,
					hora_final: form.hora_final || null,
					invitados_penya: Number(form.invitados_penya || 0),
					precio_persona: tienePreciosCargados
						? (preciosTicketsPayload.find((item) => item.precio > 0)?.precio || 0)
						: (evento.precio_persona || 0),
					precios_tickets: tienePreciosCargados ? preciosTicketsPayload : undefined,
					id_usuarios_encargados: form.usuarios_encargados.map((usuario) => usuario.value),
					id_apartado: form.id_apartado || null,
				},
			});

			if (response?.success) {
				onSaved(true);
				onClose();
				return;
			}

			setMensajeError(response?.message || response?.error || "No s'ha pogut guardar l'esdeveniment");
			setError(true);
		} catch (err) {
			console.error("Error en guardar esdeveniment:", err);
			setMensajeError("No s'ha pogut guardar l'esdeveniment");
			setError(true);
		} finally {
			setGuardando(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle className="events-create-title">
				<TipoIcon />
				Editar {tipo.label.toLowerCase()}
			</DialogTitle>
			<DialogContent dividers>
				<Grid container spacing={2} className="events-create-form">
					<Grid size={{ xs: 12 }}>
						<TextField
							fullWidth
							required
							label="Nom"
							value={form.asunto}
							onChange={(event) => updateField("asunto", event.target.value)}
							inputProps={{ maxLength: 50 }}
						/>
					</Grid>
					<Grid size={{ xs: 12 }}>
						<TextField
							fullWidth
							multiline
							minRows={3}
							label="Descripció"
							value={form.descripcion}
							onChange={(event) => updateField("descripcion", event.target.value)}
							inputProps={{ maxLength: 500 }}
						/>
					</Grid>
					{[1, 3].includes(Number(evento?.id_tipo_evento)) && <PagoMiembroFields form={form} onChange={updateField} invitadosPenya={form.invitados_penya} onInvitadosPenyaChange={(value) => updateField("invitados_penya", value)} />}
					<Grid size={{ xs: 12 }}>
						<TextField select fullWidth label="Concepte comptable" value={form.id_apartado} onChange={(event) => updateField("id_apartado", event.target.value)}>
							<MenuItem value="">Sense assignar</MenuItem>
							{conceptosContables.map((concepte) => <MenuItem key={concepte.id} value={concepte.id}>{concepte.nombre}</MenuItem>)}
						</TextField>
					</Grid>
					{usaPreciosTickets && tiposTicketsPagan.map((tipoTicket) => (
						<Grid key={tipoTicket.id} size={{ xs: 12, sm: 6 }}>
							<TextField
								fullWidth
								label={`Preu ${tipoTicket.nombre}`}
								type="number"
								value={form.precios_tickets[Number(tipoTicket.id)] || ""}
								onChange={(event) => updatePrecioTicket(tipoTicket.id, event.target.value)}
								inputProps={{ min: 0, step: "0.01" }}
							/>
						</Grid>
					))}
					{requiereEncargado && (
						<Grid size={{ xs: 12 }}>
							<Autocomplete
								multiple
								disableCloseOnSelect
								options={opTrabajadores}
								getOptionLabel={(option) => option.nombre || option.Nombre || option.label || ""}
								value={form.usuarios_encargados}
								onChange={(event, value) => updateField("usuarios_encargados", value)}
								isOptionEqualToValue={(option, value) => Number(option.value) === Number(value.value)}
								renderInput={(params) => (
									<TextField {...params} required label="Usuaris encarregats" />
								)}
							/>
						</Grid>
					)}
					<Grid size={{ xs: 12, sm: 3 }}>
						<TextField
							fullWidth
							required
							label="Data"
							type="date"
							value={form.fecha}
							onChange={(event) => updateField("fecha", event.target.value)}
							InputLabelProps={{ shrink: true }}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 3 }}>
						<TextField
							fullWidth
							required
							label="Hora d'inici"
							type="time"
							value={form.hora_inicio}
							onChange={(event) => updateField("hora_inicio", event.target.value)}
							InputLabelProps={{ shrink: true }}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 3 }}>
						<TextField
							fullWidth
							label="Hora final"
							type="time"
							value={form.hora_final}
							onChange={(event) => updateField("hora_final", event.target.value)}
							InputLabelProps={{ shrink: true }}
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={guardando}>
					Cancel·lar
				</Button>
				<Button variant="contained" startIcon={<SaveIcon />} onClick={guardar} disabled={guardando}>
					{guardando ? "Guardant..." : "Guardar"}
				</Button>
			</DialogActions>
			{error && (
				<AlertError open={error} setOpen={setError} titulo="Error" mensaje={mensajeError} />
			)}
		</Dialog>
	);
};
